import HelpOutlineIcon from '@mui/icons-material/InfoOutlined'
import { Box, Card, CardContent, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import TooltipIcon from '../../../../components/TooltipIcon'
import { useTheme } from '../../../../theme'
import { useCountersAnalyticsData } from '../../analytics/hooks/use-counters-analytics-data'

const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

const FrequencyDayChart = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { dayRows } = useCountersAnalyticsData()

  const dataArray = useMemo(() => {
    const total = dayRows.reduce((sum, row) => sum + row.value, 0)
    const valueByDay = Object.fromEntries(
      dayRows.map((row) => [row.label, row.value])
    )

    return dayKeys.map((key) => ({
      name: t(`stats.days.${key}`),
      value: total ? ((valueByDay[key] ?? 0) / total) * 100 : 0,
    }))
  }, [dayRows, t])

  const percentFormatter = (value: number) => `${Math.round(value)}%`

  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', p: 2 }}>
        <Typography color='inherit' variant='body2' textTransform={'uppercase'}>
          {t('stats.swipesByWeekday')}
        </Typography>
        <Box sx={{ pl: 1 }}>
          <TooltipIcon
            text={t('stats.tooltips.frequencyDay')}
            icon={
              <HelpOutlineIcon
                fontSize={'small'}
                sx={{ color: theme.palette.text.secondary }}
              />
            }
          />
        </Box>
      </Box>
      <CardContent
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <ResponsiveContainer width={'100%'} height={300}>
          <BarChart
            data={dataArray}
            margin={{
              top: 5,
              right: 20,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis
              dataKey='name'
              style={{
                fontFamily: theme.typography.fontFamily,
              }}
            />
            <YAxis
              tickFormatter={percentFormatter}
              style={{
                fontFamily: theme.typography.fontFamily,
              }}
            />
            <Tooltip formatter={percentFormatter} />
            <Bar
              dataKey='value'
              name={t('stats.frequencyDayShare')}
              fill='#5664D2'
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

export default FrequencyDayChart
